/* PostHog: product analytics and error tracking, and never a reason a page does not render.
 *
 * **What belongs here, and what does not.** PostHog holds events a *person* produced: the
 * identify, one `$pageview` per route change, `$pageleave`, and the named product events
 * (`api_key_minted`, `annotation_submitted`, `fleet_pod_commissioned`, …). Every number a
 * machine produced and every alert belongs to Grafana and reaches it through
 * `@sx/observability/recorder` as a span — a timing as `ui.<metric>`, a failed read or a render
 * throw as `console.failure` / `console.render_failure` (`src/lib/incident.ts`) — from which the
 * collector's `spanmetrics` connector derives every count and histogram. PostHog error tracking
 * is the debugging view for defects that stopped code: uncaught errors, unhandled rejections and
 * render throws caught by our recovery boundaries. Expected failures that the product handles are
 * not exceptions. `posthog.boundary.test.ts` holds both rules: every product-event name is a
 * literal on one allowlist, and only the incident reporter may manually capture an exception.
 * This file is the only one allowed to import the client.
 *
 * **There are no feature flags.** The console reads none, so the client never asks for any
 * (`advanced_disable_flags` below removes the `/flags` request itself) and nothing subscribes
 * to `onFeatureFlags`. Rollout and kill-switch live in a deploy; entitlement lives in
 * sx_authd grants.
 *
 * Three properties this module owes the console, in order of importance:
 *
 *  1. **It is off the product path.** Absent configuration, a failed chunk download, a
 *     blocked request — each disables capture and nothing else. There is no mode in which
 *     this file throws, so no build of the console can be white-screened by analytics.
 *  2. **Configuration is parsed once, at the boundary, into a total value.** A token and a
 *     host, or nothing: half a configuration would send product events into the void, so it
 *     disables capture rather than guessing a host. Absence is the normal state of a laptop
 *     build, a preview build and every test process — it is not an error.
 *  3. **It is reachable from a test.** This module used to disable itself whenever
 *     `MODE === "test"`, which is why 986 console cases could be green while the delivery
 *     path had never once been exercised. The project and the client loader are arguments
 *     instead, so a test drives this exact code with a fake client.
 *
 * A throw inside the subscription below would be swallowed by the router
 * (`router-core`'s `emit` wraps each listener in `try`/`catch` and logs), not by anything
 * in this file — so nothing here relies on that, and `withPostHog` stays total.
 *
 * **No automated browser is a witness to this module.** posthog-js drops every event from
 * a client it reads as a bot, and `navigator.webdriver` is one of the things it reads: a
 * Playwright run of the deployed console initializes the client, identifies the person and
 * captures the page views, and sends PostHog nothing at all. Measured on 2026-09-19 with
 * the exact options below against the real project — 0 requests to `/e/` with the default
 * `opt_out_useragent_filter`, 2 with it set. So the traversal cannot answer "does PostHog
 * receive events"; only a person's browser, or PostHog's own query API, can. Nothing here
 * opts out of that filter: a build that did would file the traversal's own navigations as
 * product usage, which is a wrong number rather than a measurement.
 *
 * Configuration reaches a build through `VITE_PUBLIC_POSTHOG_PROJECT_TOKEN` and
 * `VITE_PUBLIC_POSTHOG_HOST`, which the console image build takes as build args
 * (`infra/oci/cloud/Dockerfile.in`, `.github/actions/publish-cloud-image/action.yml`, which
 * refuses to publish a console image whose token is not `phc_*`). The project token is
 * public by construction: it ships inside the bundle every visitor downloads.
 */

import type { AnyRouter } from "@tanstack/react-router";

import { CONSOLE_WORKSPACES } from "./platform.gen";

/** Which workspace an address belongs to, longest route base first.
 *
 * `/label/audit` is Label, not whatever shorter prefix also matches; sorting by length
 * descending is what makes the answer the most specific one rather than the first one
 * declared. An address under no workspace — `/`, `/signed-out` — is `"shell"`.
 *
 * Every route base is read from the generated registry, so each one is a literal in
 * `CONSOLE_WORKSPACES`' own type. An earlier version also tested `routeBase !== "/"` here,
 * and `tsc -b` refused it (TS2367: the union and `"/"` have no overlap) — correctly, and
 * not only on a technicality. A workspace whose base really was `/` would *own* `/`, and
 * the longest-first order already stops it swallowing a deeper address, so the guard was
 * protecting against a case that cannot arise and would not be a bug if it did. The
 * comparison is gone rather than widened: a cast to silence the checker would have kept
 * dead code and thrown away the one fact the type system was offering. */
const WORKSPACE_ROUTES = [...CONSOLE_WORKSPACES]
  .map((workspace) => [workspace.routeBase, workspace.id] as const)
  .sort(([a], [b]) => b.length - a.length);

/** Exported for `posthog.test.ts`: the mapping is a fact about the product's address
 * space, so it is pinned by cases rather than only by the type checker. */
export function workspaceOf(pathname: string): string {
  for (const [routeBase, id] of WORKSPACE_ROUTES) {
    if (pathname === routeBase || pathname.startsWith(`${routeBase}/`)) {
      return id;
    }
  }
  return "shell";
}

type PostHogClient = typeof import("posthog-js")["default"];
type CaptureArguments = Parameters<PostHogClient["capture"]>;
type CaptureExceptionArguments = Parameters<PostHogClient["captureException"]>;
type IdentifyArguments = Parameters<PostHogClient["identify"]>;
type ResetArguments = Parameters<PostHogClient["reset"]>;
type GroupArguments = Parameters<PostHogClient["group"]>;
type RegisterArguments = Parameters<PostHogClient["register"]>;

/** Where product events go. Both halves or nothing — see property 2 above. */
export interface PostHogSettings {
  readonly projectToken: string;
  readonly apiHost: string;
}

/** How the client arrives. One dynamic import in production; a fake in a test.
 *
 * It stays dynamic because `scripts/chunk-budgets.ts` holds the first paint to what it
 * measured: posthog-js is a named heavy on its own lazy chunk, and analytics must not be a
 * cost of the frame it measures. */
export type PostHogLoader = () => Promise<PostHogClient>;

/** Load the client and the one extension this console permits.
 *
 * `disable_external_dependency_loading` is load-bearing under our CSP, but the ordinary
 * posthog-js entry point implements the global error hooks as an external extension. Bundle that
 * small hook into the same lazy island before the client initializes; otherwise
 * `capture_exceptions` reads as enabled while no `window.onerror` listener is installed. */
export const loadPostHog: PostHogLoader = async () => {
  await import("posthog-js/dist/exception-autocapture");
  return import("posthog-js").then(({ default: client }) => client);
};

/** The build-time variables this module reads. `import.meta.env` satisfies it, and so does
 * a literal in a test — which is the point: parsing is a function of its input, not of a
 * global. */
export interface PostHogEnv {
  readonly VITE_PUBLIC_POSTHOG_PROJECT_TOKEN?: string;
  readonly VITE_PUBLIC_POSTHOG_HOST?: string;
}

/** Read the build's PostHog configuration. Returns nothing when either half is missing. */
export function readPostHogSettings(env: PostHogEnv): PostHogSettings | undefined {
  const projectToken = env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim();
  const apiHost = env.VITE_PUBLIC_POSTHOG_HOST?.trim();
  if (!projectToken || !apiHost) return undefined;
  return { projectToken, apiHost };
}

/** Which cluster this document was served by, read from the address because the image
 * cannot know. `app.preview.<domain>` is preview; every other host — production's
 * `app.<domain>`, and a laptop, which carries no project and captures nothing — is
 * production. */
export function consoleEnvironment(hostname: string): "preview" | "production" {
  return hostname.startsWith("app.preview.") ? "preview" : "production";
}

let clientPromise: Promise<PostHogClient | undefined> | undefined;
/** The client once it has initialized, for the one synchronous question asked of it
 * (`sessionRecordingStarted`). */
let initialized: PostHogClient | undefined;

/** Start the client, once, if this build is configured for one.
 *
 * Called from the root route's module scope, which the route tree pulls in before anything
 * can capture, with `readPostHogSettings(import.meta.env)`. There is deliberately no default
 * for `settings`: a caller says which project this is, or says `undefined`, and a test that
 * means to prove the unconfigured case cannot instead be proving what its runner happened to
 * leave in `import.meta.env`. Every `posthog.*` call below is a no-op until this resolves,
 * and forever if it never does. */
export function initializePostHog(
  settings: PostHogSettings | undefined,
  load: PostHogLoader = loadPostHog,
): void {
  if (settings === undefined || clientPromise !== undefined) return;
  clientPromise = load()
    .then((client) => {
      client.init(settings.projectToken, {
        api_host: settings.apiHost,
        defaults: "2026-01-30",
        // `capturePageViews` below states every navigation, including the first, so the
        // client's own pageview capture stays off: two counters for one navigation is a
        // wrong number, not a redundant one.
        capture_pageview: false,
        // …but `$pageleave` must then be asked for by name. Its default is
        // `'if_capture_pageview'`, which reads `capture_pageview` and would silently switch
        // session duration off along with the line above. Leaving is still the client's
        // job: it is a `pagehide`/`beforeunload` fact, not a router event.
        capture_pageleave: true,
        /* Every capture this client could perform, decided here.
         *
         * The console captures only what it names, and no server setting may turn on a
         * capture the tree did not write. That is not a preference, it is the failure this
         * block closes: `/flags` carries the project's remote configuration, and remote
         * configuration carries `autocapture_opt_out` — a *decision*, needing no external
         * script and reaching the client over `connect-src` as JSON. Turning `/flags` off
         * (below) therefore does not turn autocapture off: posthog-js stops consulting the
         * server and falls through to its own default, which is `autocapture: true`. A
         * browser with no persisted `$autocapture_disabled_server_side` key — a new person,
         * a cleared profile, an incognito window — would then start sending `$autocapture`
         * click and change events carrying `$el_text` and `attr__href`: the text on the
         * screen, which here is project names, robot names and the addresses in a seats
         * table. The CSP does not stop it, because autocapture is inside the module bundle
         * and `/e/` is permitted. So each capture is stated, and the test asserts each one.
         *
         * Expected read and write failures stay in Grafana as `console.failure` spans. Exceptions
         * are narrower: the two global hooks below see only errors nobody handled, while
         * `lib/incident.ts` explicitly captures a render throw that a recovery boundary handled.
         * Console errors stay off so React's diagnostic log cannot duplicate that manual event.
         * The exception hook is bundled locally by `loadPostHog`; none of the remaining features
         * was ever reachable anyway — each loads a script from
         * us-assets.i.posthog.com that this origin's Content-Security-Policy
         * (`script-src 'self' 'wasm-unsafe-eval'`, `infra/oci/cloud/Caddyfile`) refuses —
         * except autocapture, which needs none. */
        autocapture: false,
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        capture_heatmaps: false,
        capture_performance: false,
        capture_dead_clicks: false,
        disable_session_recording: true,
        disable_surveys: true,
        // No `/flags` request at all, on any load. The console reads no feature flags, and
        // every capture the rest of that response could govern is decided above instead, so
        // what remains is a request per page load whose answer this build ignores. Checked
        // against posthog-js 1.418.5: `advanced_disable_flags` is the option that removes
        // the request (the deprecated spelling is `advanced_disable_decide`), while
        // `advanced_disable_feature_flags` keeps it and only stops flag evaluation.
        //
        // One real loss, named: the same response carries `supportedCompression`, so `/e/`
        // payloads are no longer gzipped. Product events are small and few — a page view, an
        // identify, a dozen named events a session — so this is bytes, not a capability.
        advanced_disable_flags: true,
        // The policy refuses **every** external script posthog-js loads, so the whole family
        // is declined here rather than attempted and blocked. This is the belt to the
        // per-feature braces above: those say what is captured, this says nothing is
        // fetched to capture it with. Measured against preview on 2026-09-19 (`app.preview.sentientx.io`,
        // bundle `index-BZZiJy2C.js`): the client asked us-assets.i.posthog.com for ten
        // scripts on every page load — the remote-config `config.js`, then the recorder,
        // surveys, dead-clicks and web-vitals extensions, each twice because
        // `strict_script_versioning` defaults to `'fallback'` — and the browser refused
        // nine of them, writing nine errors into the console a person has open. None of
        // those features could ever have run: session replay, surveys and dead clicks are
        // extensions this console has never asked for, and web vitals are measured by the
        // traversal's own collector (`tests/traversal/vitals.ts`) against a budget the
        // ledger reads. What survives is the one request this module actually needs:
        // capture over `/e/`. Error tracking needs no exception to this rule: its hook is part
        // of the lazy, self-hosted PostHog chunk above and sends through the same endpoint.
        disable_external_dependency_loading: true,
        debug: import.meta.env.DEV,
      });
      // One project serves both clusters because one image serves both, so the environment
      // cannot be a build-time value: preview and production run the same bytes and differ
      // only by the address they were fetched from. Registered as a super property so every
      // event carries it and a PostHog insight can exclude preview without a second project.
      client.register({ environment: consoleEnvironment(window.location.hostname) });
      initialized = client;
      return client;
    })
    .catch((error: unknown) => {
      clientPromise = undefined;
      if (import.meta.env.DEV) {
        console.error("PostHog initialization failed", error);
      }
      return undefined;
    });
}

function withPostHog(action: (client: PostHogClient) => void): void {
  void clientPromise?.then((client) => {
    if (client) action(client);
  });
}

/** Capture one `$pageview` per navigation that changes the path, until the returned
 * function is called.
 *
 * posthog-js can do this itself by monkey-patching `history.pushState`. The console has one
 * router and states the fact itself instead, for two reasons: a patched global is invisible
 * to every test in this tree — which is how "PostHog receives nothing" stayed a guess — and
 * the patch only lands when the lazy chunk resolves, so the first navigations of a slow
 * first paint fall through it. `withPostHog` queues behind the same promise, so a
 * navigation that happens before the client loads is still counted once it does. */
export function capturePageViews(router: AnyRouter): () => void {
  return router.subscribe("onResolved", ({ toLocation, pathChanged }) => {
    // A resolved navigation is not a page view. The console writes URL state through the
    // router — `?q=` as someone types in Plan Work, a selected tab in Label and Library —
    // so `onResolved` fires per keystroke, and counting those would make every search box
    // the busiest page in the product. posthog-js deduplicated on pathname; so does this.
    if (!pathChanged) return;
    // No `navigation_type`: `onResolved` does not say whether the location was pushed,
    // replaced or popped, and `@tanstack/history` keeps that distinction to itself. A
    // guessed value would be worse than an absent one.
    posthog.capture("$pageview", {
      $current_url: new URL(toLocation.href, window.location.origin).href,
      $pathname: toLocation.pathname,
      // The workspace this address belongs to, so "which part of the product do people
      // use" is a property breakdown rather than a pile of path prefixes each analyst
      // re-derives by hand — and derived from the generated registry, so a renamed or new
      // workspace is covered without touching this file.
      workspace: workspaceOf(toLocation.pathname),
    });
  });
}

/** The sole manual exception path: a render boundary replaced failed UI with recovery.
 *
 * Kept outside the product-analytics object so an ordinary caught failure cannot discover an
 * attractive generic `captureException` method. `lib/incident.ts` is the only consumer, enforced
 * by `posthog.boundary.test.ts`. */
export function captureRenderException(...args: CaptureExceptionArguments): void {
  withPostHog((client) => client.captureException(...args));
}

/** Session replay for the teleoperation route, and only for it, until the returned function is
 * called.
 *
 * The console records no replay (`disable_session_recording` above). The headset page is the
 * exception it was built for: what the operator saw is the evidence when a session goes wrong,
 * and the page's XR picture is not DOM, so `workspaces/fleet/xr/spectator.ts` draws the virtual
 * scene into a small canvas that replay captures (WebP, 3 fps). The recorder is bundled here
 * (`lazy-recorder`), never fetched, because the policy refuses PostHog's scripts. Replay also
 * needs the project's own replay switch (`session_recording_opt_in`, infra/terraform/posthog). */
export function recordTeleopReplay(): () => void {
  let stopped = false;
  void clientPromise
    ?.then(async (client) => {
      if (!client || stopped) return;
      await import("posthog-js/dist/lazy-recorder");
      if (stopped) return;
      client.set_config({
        session_recording: {
          recordCrossOriginIframes: false,
          maskAllInputs: true,
          captureCanvas: { recordCanvas: true, canvasFps: 3, canvasQuality: "0.4" },
        },
      });
      client.startSessionRecording(true);
    })
    .catch(() => undefined);
  return () => {
    stopped = true;
    withPostHog((client) => client.stopSessionRecording());
  };
}

/** Whether replay is recording now: the spectator draws only while it is. */
export function sessionRecordingStarted(): boolean {
  return initialized?.sessionRecordingStarted() ?? false;
}

export const posthog = {
  capture(...args: CaptureArguments): void {
    withPostHog((client) => client.capture(...args));
  },
  /** Attach every subsequent event to one organization and one project.
   *
   * `group` is a client-side association, not a capture: it tells this browser which
   * groups its events belong to, and PostHog applies it to everything captured afterwards.
   * It is therefore not an event and not on `posthog.boundary.test.ts`'s allowlist — it
   * adds no record of its own, it labels the ones the list already governs.
   *
   * The group's *name* is set once by the backend (`sx_service.analytics.describe`) rather
   * than here — a browser is not the authority on what an organization is called, and
   * letting each visitor assert one is how a group ends up renamed by whoever loaded the
   * page last. */
  group(...args: GroupArguments): void {
    withPostHog((client) => client.group(...args));
  },
  /** Properties every later event carries, such as the teleoperation route's station
   * session. Like `group`, a label on what the allowlist governs, not an event. */
  register(...args: RegisterArguments): void {
    withPostHog((client) => client.register(...args));
  },
  identify(...args: IdentifyArguments): void {
    withPostHog((client) => client.identify(...args));
  },
  reset(...args: ResetArguments): void {
    withPostHog((client) => client.reset(...args));
  },
};
